/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import useDebounce from '@/utils/debounce';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import type { Locale } from '@/types/i18n';
import type { PlacePrediction, PlacesAutocompleteOptions, PlacesRegionCode } from '../interface';

const LOCALE_TO_REGION: Record<Locale, PlacesRegionCode[]> = {
  [countryCodes.us]: ['us', 'pr', 'vi'],
  [countryCodes.mx]: ['mx'],
  [countryCodes.ptBr]: ['br'],
};

const EMPTY_PREDICTIONS: PlacePrediction[] = [];

export type UsePlacesAutocompleteResult = {
  predictions: PlacePrediction[];
  consumeSession: () => void;
  getPlaceCoordinates: (placeId: string) => Promise<google.maps.GeocoderResponse> | null;
};

export function usePlacesAutocomplete({
  query,
  minChars = 2,
  debounceMs = 400,
  regionCodes,
}: PlacesAutocompleteOptions): UsePlacesAutocompleteResult {
  const locale = useLocale();
  const debouncedQuery = useDebounce(query, debounceMs);
  const placesLib = useMapsLibrary('places');
  const geocodingLib = useMapsLibrary('geocoding');
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const geocoder = useMemo(() => geocodingLib && new geocodingLib.Geocoder(), [geocodingLib]);

  const effectiveRegionCodes = useMemo(
    () => regionCodes ?? LOCALE_TO_REGION[locale],
    [regionCodes, locale],
  );
  const trimmed = debouncedQuery.trim();

  useEffect(() => {
    sessionTokenRef.current = null;
  }, [effectiveRegionCodes]);

  const { data } = useQuery({
    queryKey: ['places-autocomplete', trimmed, effectiveRegionCodes],
    enabled: !!placesLib && trimmed.length >= minChars,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PlacePrediction[]> => {
      if (!placesLib) {
        return [];
      }
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
      }
      const { suggestions } = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: trimmed,
        sessionToken: sessionTokenRef.current,
        includedRegionCodes: effectiveRegionCodes,
        includedPrimaryTypes: ['geocode'],
      });

      return suggestions
        .map((suggestion): PlacePrediction | null => {
          const placePrediction = suggestion.placePrediction;
          if (!placePrediction) {
            return null;
          }
          const placeId = placePrediction.placeId ?? '';
          const fullText = placePrediction.text?.text ?? '';
          if (!placeId || !fullText) {
            return null;
          }
          const mainText = placePrediction.mainText?.text ?? fullText;
          const secondaryText = placePrediction.secondaryText?.text ?? '';
          const types = placePrediction.types;
          return { placeId, mainText, secondaryText, fullText, types };
        })
        .filter((p): p is PlacePrediction => p !== null);
    },
  });

  const consumeSession = useCallback(() => {
    sessionTokenRef.current = null;
  }, []);

  const getPlaceCoordinates = useCallback(
    (placeId: string) => {
      if (geocoder) {
        return geocoder.geocode({ placeId });
      }

      return null;
    },
    [geocoder],
  );

  // Hide suggestions as soon as the live query drops below minChars, so
  // keepPreviousData can smooth out refetches without leaking stale results.
  const isBelowMinChars = query.trim().length < minChars;

  return {
    predictions: isBelowMinChars ? EMPTY_PREDICTIONS : (data ?? EMPTY_PREDICTIONS),
    consumeSession,
    getPlaceCoordinates,
  };
}
